// Identity row and preference card shared across every contact-scoped screen.
// Mirrors the approved identity-row grid: identity card + preference card.
//
// TODO(runtime): hydrate badge, local time, and preferred channel from
// services/customer-identity + services/hubspot-contact-source once the
// read endpoints land. Preferred channel property name is still open per
// the Current Action sidecar's open questions.
//
// W26 (2026-10-20): `sinceLabel` lets a real-contact screen replace the
// "Customer since ..." line with an honest fact (last VanillaSoft call,
// HubSpot create date). The local-time separator is hidden when no
// local time is known so a real contact never shows a dangling dot.
//
// W38 (2026-10-20): the preference card renders the contact's real
// `preferredChannel` (HubSpot `preferred_channel`, an enumeration whose
// values are the display labels Text / Email / Call / No Preference /
// Do Not Contact, MEASURED via the properties API) and "not set" when
// the record carries none. The previous "Phone call" constant was a
// fixture label shown on every real contact (W30 finding 4).
//
// W40 (2026-10-21): the preference card follows the approved markup
// order (01-Current-Action v7, `section.card.preference.cc-preference-card`):
// a heading row with the PREFERRED CONTACT eyebrow on the left and the
// "Edit preferences" text button on the right, then the value, then the
// "Preference and permission are separate." line. The wrapper carries
// data-component="identity-row" so the shared parity sheet can target it.
import type { ReactNode } from 'react';
import { Icon } from './IconSprite.js';
import type { MockCustomer } from '../../data/mockData.js';

export interface IdentityRowProps {
  customer: MockCustomer;
  badgeLabel: string;
  /** Overrides the "Customer since {customerSince}" line when provided. */
  sinceLabel?: string;
  /**
   * W40: the preference card's closing line. The approved files carry
   * three variants (READ from each file's `cc-preference-card`): the
   * default below on 01, 03-10, 21, 22; "Changing a contact detail keeps
   * permissions intact." on 14; "The customer’s request guides this
   * conversation." on 16, 17, 19, 20 and both companions.
   */
  preferenceNote?: string;
}

export const PREFERRED_CHANNEL_NOT_SET = 'not set';
export const PREFERENCE_NOTE_DEFAULT = 'Preference and permission are separate.';

/** Display value for the preference card: the real label, else "not set". */
export function preferredChannelLabel(value: string | null | undefined): string {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed.length > 0 ? trimmed : PREFERRED_CHANNEL_NOT_SET;
}

export function IdentityRow({ customer, badgeLabel, sinceLabel, preferenceNote = PREFERENCE_NOTE_DEFAULT }: IdentityRowProps): ReactNode {
  const location = [customer.city, customer.stateCode].filter((v) => v && v.trim().length > 0).join(', ');
  const preferredChannel = preferredChannelLabel(customer.preferredChannel);
  return (
    <div className="identity-row" data-component="identity-row">
      <section className="card identity" data-component="customer-identity" aria-label="Customer identity">
        <div className="avatar">{customer.initials}</div>
        <div>
          <h2>{customer.fullName}</h2>
          <p className="muted">{sinceLabel ?? `Customer since ${customer.customerSince}`}</p>
        </div>
        <span className="badge">{badgeLabel}</span>
        <div className="identity-details">
          <div>
            {location}
            {customer.localTime ? (
              <>
                {' '}
                <span style={{ color: '#94a1aa' }}>· {customer.localTime}</span>
              </>
            ) : null}
          </div>
          <div>{customer.phone}</div>
          <div>{customer.email}</div>
        </div>
      </section>
      <section className="card preference cc-preference-card" data-component="contact-preference">
        <div className="cc-preference-heading">
          <span className="eyebrow">Preferred contact</span>
          <button
            className="text-btn"
            type="button"
            onClick={() => {
              // TODO(runtime): open HubSpot Contact properties quick-edit modal
              // once the proposed endpoint lands.
            }}
          >
            Edit preferences
          </button>
        </div>
        <div className="row">
          <strong data-testid="preferred-channel">
            <svg style={{ marginRight: 7, color: '#4a7183' }}>
              <use href="#message" />
            </svg>
            {preferredChannel}
          </strong>
        </div>
        <p>{preferenceNote}</p>
        {/* IconSprite reference kept alive */}
        <span style={{ display: 'none' }}>
          <Icon id="message" />
        </span>
      </section>
    </div>
  );
}
